import React, { useState, useEffect } from 'react';
import Select, { components } from 'react-select';
import classnames from 'classnames';
import lodash from 'lodash';
import styles from './MultiSelect.scss';

const customStyles = {
    valueContainer: (provided) => ({
        ...provided,
        maxHeight: '240px',
        overflowY:'auto'
    })
};

export function MultiSelect(props) {
    const [options, setOptions] = useState(props.options);
    const [selectedOptions, setSelectedOptions] = useState(props.selectedOptions);
    const [open, setOpen] = useState(false);
    const [selectAllChecked, setSelectAllChecked] = useState(false);

    const { onMenuOpen, onMenuClose, placeholder = 'Select...'} = props;

    useEffect(() => {
        setOptions(generateDropdownOptions(props.options));
        setSelectedOptions(props.selectedOptions);
        setSelectAllChecked(selectedOptions.length > 0 && selectedOptions.length === options.length);
    }, [props.options, props.selectedOptions]);

    const isOptionSelected = (targetOption, options) => {
        return lodash.some(options, (option) => option.value === targetOption);
    };

    const selectAllOptions = () => {
        const allOptions = options.filter((item) => item.value !== 'Select All');
        if (allOptions.length !== 0) {
            setSelectedOptions(allOptions);
            setSelectAllChecked(true);
            props.onChanged(allOptions);
            setOptions(generateDropdownOptions(allOptions));
        }
    };

    const clearOptions = () => {
        const allOptions = options.filter((item) => item.value !== 'Deselect All');
        setSelectedOptions([]);
        setSelectAllChecked(false);
        props.onChanged([]);
        setOptions(generateDropdownOptions(allOptions));
    };

    const generateDropdownOptions = (options) => {
        let multiSelectOptions = [];
        if (selectAllChecked) {
            multiSelectOptions.push({ label: 'Deselect All', value: 'Deselect All' });
        } else {
            multiSelectOptions.push({ label: 'Select All', value: 'Select All' });
        }
        return multiSelectOptions.concat(options);
    };

    const toggleOption = (checkedOptions) => {
        if (isOptionSelected('Select All', checkedOptions)) {
            selectAllOptions();
        } else if (isOptionSelected('Deselect All', checkedOptions)) {
            clearOptions();
        } else {
            setSelectedOptions(checkedOptions);
            setSelectAllChecked(checkedOptions.length === props.options.length);
            props.onChanged(checkedOptions);
        }
    };

    const multiValueContainer = ({selectProps, data}) => {
        const label = data.label;
        const allSelected = selectProps.value;
        const index = allSelected.findIndex((selected) => selected.label === label);
        const isLastSelected = index === allSelected.length - 1;
        const dataLabel = isLastSelected ? `${allSelected.length} selected` : '';
        return dataLabel;
    };

    const dropdownIndicator = (props) => {
        return (
            <components.DropdownIndicator {...props} data-cy="dropdownIndicator">
                {
                    open ?
                        <i className={styles.icon}>expand_less</i>
                        :
                        <i className={styles.icon}>expand_more</i>
                }
            </components.DropdownIndicator>
        );
    };

    const customComponents = () => ({
        ...(!open && { MultiValueContainer : multiValueContainer }),
        DropdownIndicator: dropdownIndicator,
    });

    const onOpen = () => {
        onMenuOpen && onMenuOpen(selectedOptions);
        setOpen(true);
    };

    const onClose = () => {
        onMenuClose && onMenuClose(selectedOptions);
        setOpen(false);
    };

    return (
        <Select
            placeholder={placeholder}
            className={classnames(styles.dropdown)}
            isMulti={true}
            options={options}
            value={selectedOptions}
            onChange={toggleOption}
            closeMenuOnSelect={false}
            backspaceRemovesValue={false}
            hideSelectedOptions={false}
            components={customComponents()}
            onMenuOpen={onOpen}
            onMenuClose={onClose}
            isClearable={false}
            styles={customStyles}
            data-cy="MultiSelect-Select"
        />
    );
}

export default MultiSelect;
